import React, { useState, useEffect } from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import ReplayIcon from '@mui/icons-material/ReplayRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import endpoints, { getBackendUrl } from '../../endpoints';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux'
import TeamTable from './tables/TeamTable'
import TeamMemberModal from './modals/TeamMemberModal'
import withAlert from '../../hoc/withAlerts'
import { setHeader } from '../../redux/headerSlice'
import Loading from '../pages/parts/Loading'
import Grow from '@mui/material/Grow';
import { ROLES, checkPermissions } from '../../roles'

function Team(props) {
    const [list, setList] = useState(null)
    const [loading, setLoading] = useState(false)
    const [syncing, setSyncing] = useState(false)
    const [selectedId, setSelectedId] = useState(null)
    const [showHidden, setShowHidden] = useState(false)

    const dispatch = useDispatch()
    const token = useSelector((state) => state.auth.token)
    const role = useSelector((state) => state.auth.userdata.role)
    const isAdmin = checkPermissions(role, ROLES.ROLE_ADMIN)

    const showAlertError = (message) => { props.showAlert(message, 'error') }
    const showAlertSuccess = (message) => { props.showAlert(message, 'success') }

    const getList = async () => {
        let uri = getBackendUrl(endpoints.team, token)

        setLoading(true)
        try {
            let res = await axios.get(uri, {
                params: showHidden ? { include_hidden: 1 } : {},
            })
            setList(res.data.items)
        } catch (e) {
            setList([])
            showAlertError('Unknown Error - get team list')
        }
        setLoading(false)
    }

    const syncAllHandle = async () => {
        setSyncing(true)
        try {
            let uri = getBackendUrl(endpoints.team_sync, token)
            let res = await axios.post(uri)
            const failed = (res.data.results ?? []).filter((r) => r.status !== 'ok')
            if (failed.length) {
                showAlertError(failed.length + ' freelancer(s) failed to sync from the agency roster')
            } else {
                showAlertSuccess('Synced ' + (res.data.count ?? 0) + ' freelancer(s) from the Upwork agency roster!')
            }
            await getList()
        } catch (e) {
            showAlertError('Unknown Error - sync team')
        }
        setSyncing(false)
    }

    const setVisibilityHandle = async (id, hidden) => {
        try {
            let uri = getBackendUrl(endpoints.team_member_visibility, token).replace('{id}', id)
            let res = await axios.put(uri, { hidden })
            showAlertSuccess(hidden ? 'Profile hidden' : 'Profile is visible again')
            await getList()
            return res.data.member
        } catch (e) {
            showAlertError(e.response?.data?.message || 'Unknown Error - update visibility')
            return null
        }
    }

    useEffect(() => {
        dispatch(setHeader({
            title: 'Team'
        }))
    }, []);

    useEffect(() => {
        getList();
    }, [showHidden]);

    return (
        <Grow in={list ? true : false}>
            <Stack sx={{ maxWidth: 1200, margin: 'auto', overflow: 'hidden', marginTop: '20px' }}>
                <TeamTable
                    items={list}
                    isAdmin={isAdmin}
                    onOpen={(item) => { setSelectedId(item.id) }}
                    onSetVisibility={setVisibilityHandle}
                    headerContent={
                        (<Stack direction="row" spacing={2} sx={{flexGrow:1}} alignItems="center">
                            <IconButton
                                size="small"
                                sx={{mb:-0.5}}
                                onClick={() => {getList()}}
                            >
                                <ReplayIcon />
                            </IconButton>
                            <FormControlLabel
                                sx={{ ml: 1 }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={showHidden}
                                        onChange={(e) => { setShowHidden(e.target.checked) }}
                                    />
                                }
                                label="Show hidden profiles"
                            />
                            <Stack sx={{flexGrow:1}}/>
                            {isAdmin ?
                            <Button
                                variant="contained"
                                startIcon={<SyncRoundedIcon />}
                                onClick={syncAllHandle}
                                disabled={syncing}
                            >
                                {syncing ? 'Syncing...' : 'Sync from Upwork'}
                            </Button>
                            : null}
                        </Stack>)
                    }
                />
                <TeamMemberModal
                    userId={selectedId}
                    isAdmin={isAdmin}
                    showAlert={props.showAlert}
                    onClose={() => { setSelectedId(null) }}
                    onSynced={() => { getList() }}
                    onSetVisibility={setVisibilityHandle}
                />
                {loading ? <Loading/> : null}
            </Stack>
        </Grow>
    );
}

export default withAlert(Team)
